import { ExternalLinkIcon } from "./Icons";

const panels = [
  {
    number: "01",
    title: "How is new supply measured?",
    summary: "Only FLR that the network has verifiably created.",
    body: <p>We listen for official inflation records emitted by Flare itself. Moving tokens between wallets, claiming an existing reward, unwrapping WFLR, or releasing vested rFLR does not create new FLR, so none of those movements are counted as new supply.</p>,
  },
  {
    number: "02",
    title: "What counts as permanently removed?",
    summary: "Only value that has crossed a point of no return.",
    body: <p>We add the base network fees destroyed by each confirmed block to native FLR received by Flare’s canonical burn address. Validator tips, ordinary transfers, tokens held in FIRE wallets, and token balances sent to a token’s zero address stay separate unless native FLR is also proven destroyed.</p>,
  },
  {
    number: "03",
    title: "How are transactions understood?",
    summary: "Receipts, known contracts and actions provide the context.",
    body: <p>Each confirmed transaction is matched to recognised Flare contracts and actions, then grouped into an easy-to-read activity family. For rFLR, a normal vested withdrawal is treated as a reward movement. An early withdrawal is also reward activity, while only its native FLR penalty reaching the burn address is counted as removed supply.</p>,
  },
  {
    number: "04",
    title: "How do the headline statistics stay trustworthy?",
    summary: "Small observations become guarded daily and 30-day totals.",
    body: <p>Confirmed blocks are rolled into daily observations, then compared across the selected window. Burn coverage shows how much realised issuance was offset. Net supply change subtracts proven removal from realised minting. If blocks are missing or a full comparison cannot be verified, the app says so and withholds any turning-point claim.</p>,
  },
] as const;

export function Methodology() {
  return (
    <section className="methodology" id="methodology" aria-labelledby="method-title">
      <div className="methodology__intro">
        <span className="eyebrow">A plain-language audit trail</span>
        <h2 id="method-title">Measured, Not Modelled</h2>
        <p>Every number begins with something Flare has confirmed on-chain. Open each layer to see how raw network activity becomes the statistics above.</p>
        <div className="method-links">
          <a href="https://dev.flare.network/support/flr" target="_blank" rel="noreferrer" aria-label="FLR supply documentation (new tab)">FLR supply <ExternalLinkIcon /></a>
          <a href="https://dev.flare.network/network/solidity-reference/IRNat" target="_blank" rel="noreferrer" aria-label="rFLR contract documentation (new tab)">How rFLR works <ExternalLinkIcon /></a>
          <a href="https://proposals.flare.network/FIP/FIP_16.html" target="_blank" rel="noreferrer" aria-label="Read FIP.16 (new tab)">Read FIP.16 <ExternalLinkIcon /></a>
        </div>
      </div>
      <div className="methodology__accordion">
        {panels.map((panel, index) => (
          <details className="methodology__item" key={panel.number} open={index === 0}>
            <summary>
              <span className="methodology__number" aria-hidden="true">{panel.number}</span>
              <span className="methodology__question"><b>{panel.title}</b><small>{panel.summary}</small></span>
              <span className="methodology__toggle" aria-hidden="true" />
            </summary>
            <div className="methodology__answer">{panel.body}</div>
          </details>
        ))}
      </div>
    </section>
  );
}
